import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useProjects } from "../context/ProjectContext";
import { toast } from "sonner";
import { BrandMark } from "../components/Brand";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft, MagnifyingGlass, X, FunnelX, Plus, PencilSimple,
  LightningSlash, Trash, ClockClockwise, CaretLeft, CaretRight, ArrowCounterClockwise,
} from "@phosphor-icons/react";

const ACTION_META = {
  create: { label: "Created", icon: Plus, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  update: { label: "Updated", icon: PencilSimple, color: "text-sky-700 bg-sky-50 border-sky-200" },
  patch: { label: "Inline change", icon: LightningSlash, color: "text-violet-700 bg-violet-50 border-violet-200" },
  delete: { label: "Deleted", icon: Trash, color: "text-red-700 bg-red-50 border-red-200" },
  restore: { label: "Restored", icon: ArrowCounterClockwise, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
};

const FIELD_LABELS = {
  module: "Module", object_id: "Object ID", object_name: "Object Name",
  object_desc: "Object Description", title: "Title", object_type: "Object Type",
  priority: "Priority", functional_owner: "Functional Owner", resource: "Resource",
  brd_status: "BRD Status", brd_date: "BRD Date",
  fdd_ready: "FDD Ready", fdd_released_date: "FDD Released Date",
  fdd_signoff_date: "FDD Sign Off Date",
  sit_status: "SIT Status", sit_completion_date: "SIT Completion Date",
  uat_status: "UAT Status", uat_completion_date: "UAT Completion Date",
  prod_migration: "Prod Migration", prod_migration_date: "Prod Migration Date",
  technical_resource: "Technical Resource", planned_dev_days: "Planned Dev Days",
  dev_start_date: "Dev Start Date", dev_end_date: "Dev End Date",
  target_date: "Target Date", development_status: "Development Status",
  actual_start_date: "Actual Start Date",
  actual_end_date: "Actual End Date", overall_status: "Overall Status",
  completion_pct: "Completion %", risk_level: "Risk Level",
  risk_description: "Risk Description", delay_days: "Delay (Days)",
  scope_of_works: "Scope of Works", comments: "Comments",
};

const ALL = "__all__";

function fmtVal(v) {
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function Activity() {
  const { projectId, project } = useProjects();
  const [entries, setEntries] = useState([]);
  const [actors, setActors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(30);
  const [filters, setFilters] = useState({
    actor: "", action: "", date_from: "", date_to: "", search: "",
  });

  const query = useMemo(() => {
    const p = { page, page_size: pageSize };
    if (projectId) p.project_id = projectId;
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [filters, page, pageSize, projectId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit", { params: query });
      setEntries(data.entries || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      if (data.page && data.page !== page) setPage(data.page);
    } catch (e) {
      toast.error("Failed to load activity");
    } finally {
      setLoading(false);
    }
  }, [query, page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/audit/actors", { params: projectId ? { project_id: projectId } : {} })
      .then(({ data }) => setActors(data.actors || []))
      .catch(() => {});
  }, [projectId]);

  // Reset to page 1 whenever filters change
  useEffect(() => { setPage(1); }, [filters]);

  const activeFilterCount = Object.values(filters).filter((v) => v).length;
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="activity-page">
      <header className="h-16 border-b border-[#E5E5E5] bg-white flex items-center px-6 justify-between shrink-0">
        <div className="flex items-center gap-3">
          <BrandMark />
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-black tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
              PROJECT TRACKER
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
              Activity Log{project ? ` · ${project.name}` : ""}
            </span>
          </div>
        </div>
        <Link to="/">
          <Button variant="outline" size="sm" data-testid="back-to-dashboard-btn" className="h-9">
            <ArrowLeft size={16} weight="bold" className="mr-2" />
            Back to Tracker
          </Button>
        </Link>
      </header>

      <div className="flex-1 p-6 max-w-5xl w-full mx-auto space-y-5">
        <div>
          <p
            className="text-[10px] uppercase tracking-[0.3em] text-neutral-500"
            style={{ fontFamily: "IBM Plex Mono, monospace" }}
          >
            Audit trail
          </p>
          <h1 className="text-3xl font-black tracking-tight mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>
            Global Activity
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            Every field change across the tracker — filter by user, action, or date to investigate exactly who did what and when.
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 flex flex-col md:flex-row md:items-end gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              data-testid="activity-search-input"
              className="pl-9 h-10 bg-white"
              placeholder="Search actor email or field name..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
            {filters.search ? (
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700"
                onClick={() => setFilters({ ...filters, search: "" })}
              >
                <X size={14} weight="bold" />
              </button>
            ) : null}
          </div>

          <Select
            value={filters.actor || ALL}
            onValueChange={(v) => setFilters({ ...filters, actor: v === ALL ? "" : v })}
          >
            <SelectTrigger data-testid="activity-actor-filter" className="h-10 w-[220px] bg-white">
              <SelectValue placeholder="All users" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All users</SelectItem>
              {actors.map((a) => (
                <SelectItem key={a} value={a}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.action || ALL}
            onValueChange={(v) => setFilters({ ...filters, action: v === ALL ? "" : v })}
          >
            <SelectTrigger data-testid="activity-action-filter" className="h-10 w-[160px] bg-white">
              <SelectValue placeholder="All actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All actions</SelectItem>
              <SelectItem value="create">Created</SelectItem>
              <SelectItem value="update">Updated</SelectItem>
              <SelectItem value="patch">Inline change</SelectItem>
              <SelectItem value="delete">Deleted</SelectItem>
              <SelectItem value="restore">Restored</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2">
            <Input
              type="date"
              data-testid="activity-date-from"
              className="h-10 bg-white w-[150px]"
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              placeholder="From"
            />
            <span className="text-xs text-neutral-400">→</span>
            <Input
              type="date"
              data-testid="activity-date-to"
              className="h-10 bg-white w-[150px]"
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              placeholder="To"
            />
          </div>

          <Button
            variant="outline"
            size="sm"
            disabled={activeFilterCount === 0}
            onClick={() => setFilters({ actor: "", action: "", date_from: "", date_to: "", search: "" })}
            data-testid="activity-clear-filters-btn"
            className="h-10"
          >
            <FunnelX size={16} weight="bold" className="mr-2" />
            Clear
            {activeFilterCount > 0 ? (
              <span className="ml-2 text-xs bg-neutral-900 text-white rounded-full h-5 w-5 flex items-center justify-center">
                {activeFilterCount}
              </span>
            ) : null}
          </Button>
        </div>

        {/* Timeline */}
        <div
          className="bg-white border border-[#E5E5E5] rounded-lg p-6"
          data-testid="activity-list"
        >
          <div className="text-xs text-neutral-500 mb-4">
            <span className="font-mono tabular-nums text-neutral-900">{total}</span>{" "}
            {total === 1 ? "entry" : "entries"}
            {loading ? " · loading…" : ""}
          </div>

          {entries.length === 0 && !loading ? (
            <div className="text-sm text-neutral-500 py-16 text-center flex flex-col items-center gap-2">
              <ClockClockwise size={32} weight="thin" className="text-neutral-400" />
              No activity matches these filters.
            </div>
          ) : (
            <div className="relative pl-6">
              <div className="absolute left-[9px] top-2 bottom-2 w-px bg-neutral-200" aria-hidden="true" />
              {entries.map((e, idx) => {
                const meta = ACTION_META[e.action] || ACTION_META.update;
                const Icon = meta.icon;
                return (
                  <div key={e.id} className="relative pb-6" data-testid={`activity-entry-${idx}`}>
                    <div className={`absolute -left-[15px] top-0.5 h-4 w-4 rounded-full border-2 border-white flex items-center justify-center ${meta.color}`}>
                      <Icon size={9} weight="bold" />
                    </div>
                    <div className="flex items-baseline justify-between gap-3 mb-1 flex-wrap">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span
                          className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${meta.color}`}
                          data-testid={`activity-action-${idx}`}
                        >
                          {meta.label}
                        </span>
                        <span className="text-sm font-medium text-neutral-900">{e.actor_email}</span>
                        {e.actor_role ? (
                          <span className="text-[10px] uppercase tracking-wider text-neutral-400">{e.actor_role}</span>
                        ) : null}
                        <span className="text-xs text-neutral-500">on</span>
                        {e.task ? (
                          <span
                            className="text-xs font-mono px-1.5 py-0.5 bg-neutral-100 rounded"
                            data-testid={`activity-task-${idx}`}
                          >
                            {(e.task.module || "").toUpperCase()}{e.task.module ? " · " : ""}
                            {e.task.object_id || e.task_id.slice(0, 6)}
                          </span>
                        ) : (
                          <span className="text-xs text-neutral-400 italic">deleted task ({e.task_id.slice(0, 6)})</span>
                        )}
                        {e.task?.title ? (
                          <span className="text-xs text-neutral-600 max-w-[280px] truncate">
                            {e.task.title}
                          </span>
                        ) : null}
                      </div>
                      <span
                        className="text-xs text-neutral-500 shrink-0"
                        title={new Date(e.timestamp).toLocaleString()}
                      >
                        {timeAgo(e.timestamp)}
                      </span>
                    </div>
                    {e.changes && e.changes.length > 0 ? (
                      <div className="space-y-1 mt-2">
                        {e.changes.map((c, i) => (
                          <div
                            key={i}
                            className="text-xs bg-neutral-50 border border-neutral-200 rounded px-2.5 py-1.5 flex items-center gap-2 flex-wrap"
                          >
                            <span className="font-semibold text-neutral-700 uppercase tracking-wider text-[10px]">
                              {FIELD_LABELS[c.field] || c.field}
                            </span>
                            <span className="text-neutral-500 line-through max-w-[240px] truncate">
                              {fmtVal(c.old)}
                            </span>
                            <span className="text-neutral-400">→</span>
                            <span className="text-[#0A0A0A] font-medium max-w-[240px] truncate">
                              {fmtVal(c.new)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 ? (
          <div className="flex items-center justify-end gap-2 pb-6" data-testid="activity-pagination">
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              disabled={!canPrev}
              onClick={() => setPage(page - 1)}
              data-testid="activity-prev-btn"
            >
              <CaretLeft size={14} weight="bold" className="mr-1" />
              Prev
            </Button>
            <span className="text-xs text-neutral-500 font-mono tabular-nums px-2">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              disabled={!canNext}
              onClick={() => setPage(page + 1)}
              data-testid="activity-next-btn"
            >
              Next
              <CaretRight size={14} weight="bold" className="ml-1" />
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
